'use client'

import { useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'

export function AnimatedBackground() {
  const { scrollYProgress } = useScroll()

  // Transform scroll progress for different elements
  const blob1Y = useTransform(scrollYProgress, [0, 1], [0, -200])
  const blob2Y = useTransform(scrollYProgress, [0, 1], [0, 150])
  const blob3Y = useTransform(scrollYProgress, [0, 1], [0, -100])
  const dotsOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.6])

  // Generate random positions for dots only on client to avoid hydration mismatch
  const [dots, setDots] = useState<Array<{
    id: number
    x: number
    y: number
    size: number
    delay: number
    duration: number
  }>>([])

  useEffect(() => {
    // Only generate dots on client side to avoid hydration mismatch
    setDots(
      Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        delay: Math.random() * 2,
        duration: Math.random() * 3 + 2,
      }))
    )
  }, [])

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      {/* Animated Green Dots */}
      <motion.div 
        className="absolute inset-0"
        style={{ opacity: dotsOpacity }}
      >
        {dots.map((dot) => (
          <motion.div
            key={dot.id}
            className="absolute rounded-full bg-green-400/40 dark:bg-green-500/30"
            style={{
              left: `${dot.x}%`,
              top: `${dot.y}%`,
              width: `${dot.size}px`,
              height: `${dot.size}px`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.4, 0.8, 0.4],
            }}
            transition={{
              duration: dot.duration,
              delay: dot.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </motion.div>

      {/* Floating Blobs with Scroll Effect */}
      <motion.div
        style={{ y: blob1Y }}
        className="absolute top-20 left-10 w-72 h-72 bg-green-300/20 dark:bg-green-600/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        style={{ y: blob2Y }}
        className="absolute top-1/2 right-20 w-96 h-96 bg-green-400/15 dark:bg-green-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        style={{ y: blob3Y }}
        className="absolute bottom-20 left-1/3 w-80 h-80 bg-green-500/20 dark:bg-green-700/15 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.1, 1],
          x: [0, 40, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Additional smaller blobs */}
      <motion.div
        className="absolute top-1/3 right-1/4 w-64 h-64 bg-green-200/25 dark:bg-green-800/15 rounded-full blur-2xl"
        animate={{
          scale: [1, 1.4, 1],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute bottom-1/4 right-10 w-56 h-56 bg-green-300/20 dark:bg-green-600/10 rounded-full blur-2xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -20, 0],
          y: [0, -40, 0],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Gradient Orbs */}
      <motion.div
        className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-br from-green-400/30 to-green-600/20 dark:from-green-500/20 dark:to-green-700/15 rounded-full blur-2xl"
        animate={{
          scale: [1, 1.5, 1],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Pulsing Rings */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute top-1/2 left-1/2 w-32 h-32 border-2 border-green-400/20 dark:border-green-500/15 rounded-full"
          style={{
            x: '-50%',
            y: '-50%',
            left: `${20 + i * 30}%`,
            top: `${30 + i * 20}%`,
          }}
          animate={{
            scale: [1, 2, 1],
            opacity: [0.3, 0, 0.3],
          }}
          transition={{
            duration: 4 + i,
            repeat: Infinity,
            ease: 'easeOut',
            delay: i * 1.5,
          }}
        />
      ))}

      {/* Animated Grid Pattern (subtle) */}
      <div className="absolute inset-0 opacity-5 dark:opacity-3">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>
    </div>
  )
}
